import { Link } from "react-router-dom";
import { renderGlyph } from "./toolGlyphs.jsx";
import { gradientFor } from "./toolGradients.js";
import { TOKENS } from "./tokens.js";

/**
 * Canonical tool card — single source for every suite's tool list.
 *
 * LOCKED treatment (reconciled Icon & Branding spec):
 *   - Grid TILE: SOLID suite-gradient rounded tile (tool hue → suite
 *     master hue, gradientFor). Not the old transparent/thin-border
 *     tile (under-legible at thumbnail; the reason the solid container
 *     was adopted).
 *   - GLYPH: white V33 line glyph on the gradient tile, geometry
 *     unchanged, stroke at the V33 working weight (~1.9). Do NOT
 *     thicken to "improve" legibility (the §4 thickening lesson).
 *   - NAME: separate label BELOW the tile, READABLE BY DEFAULT —
 *     opacity ~0.9 at rest, full on hover, ALWAYS full on touch. The
 *     old hover-dependent rest-dim (.45) is removed: students scan,
 *     labels must be immediately readable (spec §2.8 / Priority 3).
 *   - `signature` keeps the Phys hero-tool emphasis (stronger ring).
 *   - No glyph match → still renders name as text on the tile.
 *
 * One scoped <style> (stable class) because :hover / media queries
 * can't be inline.
 */
export function ToolCard({ to, name, desc, accent = "#1C1E2E", signature = false }) {
  const { from, to: end, id } = gradientFor(name, to);
  const cls = "tc-" + id;
  const tileId = id + "-tile";

  const glyph = renderGlyph(name, {
    size: 40,
    stroke: `url(#${tileId})`,
    strokeWidth: 1.9,
    defs: (
      <defs>
        {/* V57.4 — gradientUnits="userSpaceOnUse" with explicit viewBox
           coordinates. Default objectBoundingBox makes the gradient apply
           PER PATH, so multi-path glyphs (the reference book glyph has 9
           paths) get fragmented gradients that read as flat or noisy.
           userSpaceOnUse bounds the gradient to the whole 24×24 SVG so
           all paths sample from one unified gradient and the glyph reads
           as a coherent gradient image. */}
        <linearGradient id={tileId} gradientUnits="userSpaceOnUse" x1="0" y1="0" x2="24" y2="24">
          <stop offset="0" stopColor={from} />
          <stop offset="1" stopColor={end} />
        </linearGradient>
      </defs>
    ),
  });

  // Tools = gradient glyph on TRANSPARENT tile (the no-plate model).
  // Solid gradient tiles are PARENT-only (SuiteMark), never tools.
  const ring = signature ? `${end}66` : "rgba(255,255,255,0.10)";

  return (
    <div className={cls} style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 8 }}>
      <style>{`
        .${cls} { text-align: center; }
        .${cls} .tc-name { color: #C8CADF; font-size: 12.5px; font-weight: 500; opacity: ${TOKENS.labelOpacityRest}; transition: color .15s ease, opacity .15s ease; }
        @media (hover: hover) {
          .${cls}:hover .tc-name { opacity: 1; color: #E8E9F3; }
        }
        @media (hover: none) {
          .${cls} .tc-name { opacity: 1; }
        }
        .${cls} .tc-tile { transition: transform .12s ease, box-shadow .15s ease; }
        @media (hover: hover) {
          .${cls}:hover .tc-tile { transform: translateY(-2px); box-shadow: 0 6px 18px -6px ${end}88; }
        }
      `}</style>
      <Link
        to={to}
        aria-label={name}
        className="tc-tile"
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          width: "100%",
          aspectRatio: "1 / 1",
          maxWidth: 132,
          background: "transparent",
          border: `1px solid ${ring}`,
          borderRadius: TOKENS.tileRadius,
          textDecoration: "none",
        }}
      >
        {glyph || (
          <span style={{ color: from, fontSize: 13, padding: 12, textAlign: "center", fontWeight: 600 }}>{name}</span>
        )}
      </Link>
      <div className="tc-name">{name}</div>
    </div>
  );
}

export default ToolCard;
